import { useFormContext } from "react-hook-form";

const defaultMessages = {
  required: "Fill this in to continue.",
  maxLength: "That's longer than we can accept.",
  minLength: "That's shorter than expected.",
  min: "That number is too low.",
  max: "That number is too high.",
};

export function useFieldError(name) {
  const { getFieldState, formState } = useFormContext();
  const { error } = getFieldState(name, formState);
  if (!error) return null;
  return error.message || defaultMessages[error.type] || "Check this field.";
}

export function FieldError({ name, id }) {
  const message = useFieldError(name);
  if (!message) return null;
  return (
    <p className="reg-error" id={id} role="alert">
      {message}
    </p>
  );
}

export default function Field({
  name,
  label,
  hint,
  rules,
  as = "input",
  options,
  placeholder,
  className = "",
  hideError = false,
  ...inputProps
}) {
  const { register } = useFormContext();
  const error = useFieldError(name);
  const id = name.replace(/[^a-zA-Z0-9]/g, "-");
  const describedBy = [
    hint && `${id}-hint`,
    error && !hideError && `${id}-error`,
  ]
    .filter(Boolean)
    .join(" ");
  const shared = {
    id,
    "aria-invalid": error ? true : undefined,
    "aria-describedby": describedBy || undefined,
    ...register(name, rules),
  };

  return (
    <div className={`reg-field ${className}`}>
      <label htmlFor={id}>
        {label}
        {!rules?.required && <span className="reg-optional"> (optional)</span>}
      </label>
      {as === "select" ? (
        <select {...shared} defaultValue="">
          <option value="" disabled>
            {placeholder || "Choose one"}
          </option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ) : as === "textarea" ? (
        <textarea rows={3} placeholder={placeholder} {...shared} />
      ) : (
        <input placeholder={placeholder} {...inputProps} {...shared} />
      )}
      {hint && (
        <p className="reg-hint" id={`${id}-hint`}>
          {hint}
        </p>
      )}
      {!hideError && <FieldError name={name} id={`${id}-error`} />}
    </div>
  );
}
